import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Loader2, LifeBuoy, User, Paperclip } from "lucide-react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, sendOtp, verifyOtp, signup, selectAccount, uploadFile, chatThread, chatVcard } from "@/lib/api";

function dealerSlugFromPath() {
  const m = window.location.pathname.match(/\/d\/([^/?#]+)/);
  return m ? m[1] : "orevital";
}

function buildVcardFile(c) {
  const lines = [
    "BEGIN:VCARD", "VERSION:3.0",
    `FN:${c.name || c.company || "OreVital"}`,
    c.company ? `ORG:${c.company}` : "",
    c.phone ? `TEL;TYPE=CELL:${c.phone}` : "",
    c.email ? `EMAIL:${c.email}` : "",
    c.address ? `ADR;TYPE=WORK:;;${c.address}` : "",
    "END:VCARD",
  ].filter(Boolean);
  return new Blob([lines.join("\n")], { type: "text/vcard" });
}

function ContactCard({ c, onChat }) {
  const save = () => {
    const url = URL.createObjectURL(buildVcardFile(c));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(c.name || "contact").replace(/\s+/g, "-")}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="rounded-2xl border border-emerald-200 bg-white overflow-hidden w-full" data-testid="chat-vcard">
      <div className="bg-emerald-50 px-3 py-2.5 flex items-center gap-2.5">
        <div className="h-10 w-10 rounded-full bg-white border border-emerald-200 overflow-hidden flex items-center justify-center shrink-0">
          {c.logo ? <img src={c.logo} alt="" className="h-full w-full object-contain p-0.5" /> : <User size={18} className="text-emerald-700" />}
        </div>
        <div className="min-w-0">
          <div className="font-bold text-sm text-emerald-900 truncate">{c.name}</div>
          <div className="text-[11px] text-emerald-700 truncate">{c.tagline || c.company}</div>
        </div>
      </div>
      <div className="p-3 space-y-2 text-xs text-zinc-700">
        {c.address && <div><span className="font-semibold">📍 </span>{c.address}</div>}
        {c.phone && <div><span className="font-semibold">📞 </span><a href={`tel:${c.phone}`} className="text-emerald-700 hover:underline">{c.phone}</a></div>}
        {c.email && <div className="truncate"><span className="font-semibold">✉️ </span><a href={`mailto:${c.email}`} className="text-emerald-700 hover:underline">{c.email}</a></div>}
        {c.hours && <div><span className="font-semibold">🕘 </span>{c.hours}</div>}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1 rounded-lg bg-zinc-900 text-white px-3 py-1.5 text-[11px] font-semibold" data-testid="vcard-call">Call</a>}
          {c.email && <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 px-3 py-1.5 text-[11px] font-semibold" data-testid="vcard-email">Email</a>}
          <button onClick={save} className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 px-3 py-1.5 text-[11px] font-semibold" data-testid="vcard-save">Save vCard</button>
        </div>
        <button onClick={onChat} className="w-full mt-1 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold" data-testid="vcard-chat-btn">
          💬 Chat with our team (we'll text you)
        </button>
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const { user, adoptUser } = useAuth();
  const location = useLocation();
  const callerRecId = user?.user_record_id || user?.record_id || "";
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [identified, setIdentified] = useState(false);
  const [userRecId, setUserRecId] = useState("");
  const [ticketRecId, setTicketRecId] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Identity gate state machine: "phone" -> "code" -> "signup" -> identified.
  // The gate is now OPT-IN: a public visitor can chat freely with Molly and is
  // only asked for a phone (SMS) when they explicitly want to reach support.
  const [wantSupport, setWantSupport] = useState(false);
  const [pendingQ, setPendingQ] = useState("");
  const [gateStep, setGateStep] = useState("phone");
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [signupForm, setSignupForm] = useState({ first_name: "", last_name: "", email: "" });
  const [gateBusy, setGateBusy] = useState(false);
  const [gateErr, setGateErr] = useState("");

  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const seenAdminMsgs = useRef(new Set());

  // Poll for agent replies once a support ticket exists.
  useEffect(() => {
    if (!ticketRecId || !open) return;
    let alive = true;
    const tick = async () => {
      try {
        const thread = await chatThread(ticketRecId);
        if (!alive) return;
        const fresh = thread.filter((m) => m.sender === "admin" && !seenAdminMsgs.current.has(m.id));
        if (fresh.length) {
          fresh.forEach((m) => seenAdminMsgs.current.add(m.id));
          setMessages((x) => [...x, ...fresh.map((m) => ({ role: "bot", text: m.message }))]);
        }
      } catch { /* ignore */ }
    };
    const id = setInterval(tick, 8000);
    return () => { alive = false; clearInterval(id); };
  }, [ticketRecId, open]);

  const botName = config?.bot_name || "Molly";

  const greetByName = (name, bot = config?.bot_name) =>
    name
      ? `Hi ${name}! 👋 I'm ${bot || "Molly"}. How can I help you today?`
      : `Hi! I'm ${bot || "Molly"}. How can I help you today?`;

  // Load config + greeting the first time the panel opens.
  useEffect(() => {
    if (!open || config) return;
    const dealer = dealerSlugFromPath();
    api
      .get(
        `/postcue/chat/config?dealer=${encodeURIComponent(dealer)}${
          callerRecId ? `&caller_user_record_id=${encodeURIComponent(callerRecId)}` : ""
        }`
      )
      .then(({ data }) => {
        setConfig(data);
        if (!data.need_identity && callerRecId) {
          setIdentified(true);
          setUserRecId(callerRecId);
          setMessages([{ role: "bot", text: greetByName(data.caller_first_name || user?.first_name, data.bot_name) }]);
        } else {
          setMessages([{ role: "bot", text: data.greeting }]);
        }
      })
      .catch(() => {
        setConfig({ bot_name: "Molly", greeting: "Hi! How can I help?", need_identity: false });
        setMessages([{ role: "bot", text: "Hi! How can I help you today?" }]);
        setIdentified(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, config, callerRecId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const pushBot = (text) => setMessages((m) => [...m, { role: "bot", text }]);

  // ── Step 1: phone → send SMS code ──────────────────────────────────────
  const submitPhone = async (e) => {
    e?.preventDefault();
    if (!phone.trim() || gateBusy) return;
    setGateBusy(true);
    setGateErr("");
    try {
      await sendOtp(phone.trim());
      setGateStep("code");
      pushBot(`I just texted a 6-digit code to ${phone.trim()}. Enter it below to continue.`);
    } catch (err) {
      setGateErr(
        err?.response?.data?.detail ||
          "Couldn't send the code. Check the number and try again."
      );
    } finally {
      setGateBusy(false);
    }
  };

  // ── Step 2: verify code ────────────────────────────────────────────────
  const submitCode = async (e) => {
    e?.preventDefault();
    if (otp.trim().length < 4 || gateBusy) return;
    setGateBusy(true);
    setGateErr("");
    try {
      const data = await verifyOtp(phone.trim(), otp.trim());
      if (data?.success === false) {
        setGateErr(data.message || "Invalid code. Try again.");
        return;
      }
      if (data.signup_required) {
        setGateStep("signup");
        pushBot("Looks like you're new here — just a couple details and we're set.");
        return;
      }
      if (data.accounts_required) {
        // Multiple accounts share this phone — pick the first for support chat.
        const acc = data.accounts?.[0];
        const finalUser = await selectAccount(data.select_token, acc.record_id);
        finishLogin(finalUser);
        return;
      }
      finishLogin(data.user || data);
    } catch (err) {
      setGateErr(err?.response?.data?.detail || "Couldn't verify the code. Try again.");
    } finally {
      setGateBusy(false);
    }
  };

  // ── Step 3 (new customer): create account, email required ──────────────
  const submitSignup = async (e) => {
    e?.preventDefault();
    if (gateBusy) return;
    if (!signupForm.first_name.trim()) {
      setGateErr("First name is required.");
      return;
    }
    if (!signupForm.email.trim() || !signupForm.email.includes("@")) {
      setGateErr("A valid email is required.");
      return;
    }
    setGateBusy(true);
    setGateErr("");
    try {
      const data = await signup({
        phone: phone.trim(),
        phone_verified: true,
        first_name: signupForm.first_name.trim(),
        last_name: signupForm.last_name.trim(),
        email: signupForm.email.trim(),
      });
      finishLogin(data.user || data);
    } catch (err) {
      setGateErr(err?.response?.data?.detail || "Couldn't create your account. Try again.");
    } finally {
      setGateBusy(false);
    }
  };

  const finishLogin = (sessionUser) => {
    if (!sessionUser) return;
    adoptUser(sessionUser);
    const rid = sessionUser.user_record_id || sessionUser.record_id || "";
    setUserRecId(rid);
    setIdentified(true);
    pushBot(greetByName(sessionUser.first_name));
    // If they were on their way to support, connect them now.
    if (wantSupport) {
      setWantSupport(false);
      escalate(pendingQ || "Customer support request", rid);
      setPendingQ("");
    }
  };

  // ── Request support → ALWAYS show the contact v-card first, then route:
  //    identified users also get a support ticket logged; anonymous visitors
  //    get the phone/SMS gate so the team can follow up. ───────────────────
  const requestSupport = async (question) => {
    try {
      const c = await chatVcard(dealerSlugFromPath());
      setMessages((m) => [...m, { role: "bot", vcard: c }]);
    } catch {
      // ignore — fall back to the routing below
    }
    if (identified || userRecId) {
      escalate(question);
      return;
    }
    setPendingQ(question || "Customer support request");
    setGateStep("phone");
    setWantSupport(true);
    pushBot("📞 Reach us anytime using the card above — or drop your number below and our team will text you back.");
  };

  // ── Escalate to human support ──────────────────────────────────────────
  const escalate = async (question, recIdOverride) => {
    const rid = recIdOverride || userRecId;
    if (!rid) {
      // Not identified yet → open the phone/SMS gate so the team can follow up.
      setPendingQ(question || "Customer support request");
      setWantSupport(true);
      setGateStep("phone");
      pushBot("Happy to connect you with our team! 💬 First, pop in your mobile number below — we'll text a quick code so support can follow up with you.");
      return;
    }
    try {
      const { data } = await api.post(`/postcue/chat/message`, {
        user_record_id: rid,
        message: question || "Customer support request",
        ticket_record_id: ticketRecId || undefined,
        category: "Support",
        dealer_owner_record_id: config?.owner_is_dealer ? config?.owner_record_id : undefined,
      });
      setTicketRecId(data.ticket_record_id);
      pushBot("I've sent your message to our support team — they'll reach out to you shortly. 💌");
    } catch {
      pushBot("Couldn't reach support right now. Please try again.");
    }
  };

  // ── Photo upload → store in R2, attach to a support ticket ─────────────
  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!userRecId) {
      escalate("Shared a photo");
      return;
    }
    setUploading(true);
    try {
      const res = await uploadFile(file, userRecId, "support");
      setMessages((m) => [...m, { role: "user", text: "", image: res.url }]);
      const { data } = await api.post(`/postcue/chat/message`, {
        user_record_id: userRecId,
        message: "Shared a photo",
        image_url: res.url,
        ticket_record_id: ticketRecId || undefined,
        category: "Support",
        dealer_owner_record_id: config?.owner_is_dealer ? config?.owner_record_id : undefined,
      });
      setTicketRecId(data.ticket_record_id);
      pushBot("Thanks for the photo — our team can see it now. 📎");
    } catch {
      pushBot("Couldn't upload that photo. Please try a smaller image.");
    } finally {
      setUploading(false);
    }
  };

  // ── Ask a question (AI → escalate) ─────────────────────────────────────
  const send = async (e) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setSending(true);
    try {
      const { data } = await api.post(`/postcue/chat/ask`, {
        question: q,
        dealer_id: config?.owner_user_id || undefined,
        is_dealer: config?.role === "admin" || config?.role === "approved",
        caller_user_record_id: callerRecId || undefined,
      });
      if (data.answered) {
        setMessages((m) => [
          ...m,
          { role: "bot", text: data.answer, products: data.products || [] },
        ]);
      } else {
        pushBot("Let me connect you with our team — here are the best ways to reach us 👇");
        await requestSupport(q);
      }
    } catch {
      pushBot("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const showGate = config?.need_identity && !identified && wantSupport;
  const lastUserText = messages.filter((m) => m.role === "user" && m.text).slice(-1)[0]?.text;

  // Hide the public Molly chat bubble inside the dealer portal (/app/*) so it
  // doesn't overlap the bottom nav / Events button. Dealers use the support bell.
  if (location.pathname.startsWith("/app")) return null;

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-launcher"
        aria-label="Chat with us"
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg flex items-center justify-center transition-colors"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          className="fixed bottom-24 right-5 z-[60] w-[min(94vw,380px)] h-[min(72vh,560px)] flex flex-col rounded-2xl bg-white shadow-2xl border border-zinc-200 overflow-hidden"
          data-testid="chat-panel"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
            <div className="h-10 w-10 rounded-full bg-white/20 overflow-hidden flex items-center justify-center shrink-0">
              {config?.bot_photo ? (
                <img src={config.bot_photo} alt="" className="h-full w-full object-cover" />
              ) : (
                <User size={20} />
              )}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-sm" data-testid="chat-bot-name">
                {botName}
              </div>
              <div className="text-[11px] text-emerald-100 truncate">
                {config?.company || "OreVital & Mindful Minerals"}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-[#f6f7f4]">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${m.role}`}
              >
                {m.vcard ? (
                  <div className="max-w-[90%] w-full">
                    <ContactCard c={m.vcard} onChat={() => escalate("Customer support request")} />
                  </div>
                ) : (
                <div className="max-w-[82%] flex flex-col gap-1.5">
                <div
                  className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-emerald-600 text-white rounded-br-sm"
                      : "bg-white text-zinc-800 border border-zinc-100 rounded-bl-sm"
                  }`}
                >
                  {m.image ? (
                    <img
                      src={m.image}
                      alt="attachment"
                      className="rounded-lg max-h-44 object-cover"
                      data-testid="chat-msg-image"
                    />
                  ) : (
                    m.text
                  )}
                </div>
                {Array.isArray(m.products) && m.products.length > 0 && (
                  <div className="flex flex-col gap-1.5" data-testid="chat-products">
                    {m.products.map((p) => (
                      <a
                        key={p.id}
                        href={
                          p.in_stock
                            ? `/d/${encodeURIComponent(dealerSlugFromPath())}/product/${encodeURIComponent(p.slug)}`
                            : undefined
                        }
                        className={`flex items-center gap-2.5 rounded-xl border bg-white p-2 transition ${
                          p.in_stock
                            ? "border-zinc-200 hover:border-emerald-400 cursor-pointer"
                            : "border-zinc-100 opacity-70 cursor-default"
                        }`}
                        data-testid={`chat-product-${p.id}`}
                      >
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.name}
                            className="h-11 w-11 rounded-lg object-cover shrink-0"
                          />
                        ) : (
                          <div className="h-11 w-11 rounded-lg bg-zinc-100 shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-[13px] font-semibold text-zinc-900 truncate">
                            {p.name}
                          </div>
                          <div className="text-[11px] text-zinc-500">
                            ${Number(p.price || 0).toFixed(2)}
                          </div>
                        </div>
                        {p.in_stock ? (
                          <span className="shrink-0 rounded-lg bg-emerald-600 text-white text-[11px] font-bold px-3 py-1.5">
                            Shop
                          </span>
                        ) : (
                          <span className="shrink-0 rounded-lg bg-rose-50 text-rose-600 border border-rose-200 text-[10px] font-semibold px-2 py-1.5">
                            Out of stock
                          </span>
                        )}
                      </a>
                    ))}
                  </div>
                )}
                </div>
                )}
              </div>
            ))}
            {(sending || uploading) && (
              <div className="flex justify-start">
                <div className="bg-white border border-zinc-100 rounded-2xl px-3 py-2">
                  <Loader2 size={14} className="animate-spin text-emerald-600" />
                </div>
              </div>
            )}
          </div>

          {/* Identity gate (public visitors) — phone → code → signup */}
          {showGate ? (
            <div className="border-t border-zinc-200 p-3 bg-white" data-testid="chat-identity-form">
              {gateStep === "phone" && (
                <form onSubmit={submitPhone} className="space-y-2">
                  <p className="text-[11px] text-zinc-500">
                    Enter your mobile number — we'll text you a code to confirm it's you.
                  </p>
                  <input
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Mobile number *"
                    type="tel"
                    className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm"
                    data-testid="chat-phone-input"
                    required
                  />
                  {gateErr && <p className="text-[11px] text-red-600" data-testid="chat-gate-error">{gateErr}</p>}
                  <button
                    type="submit"
                    disabled={gateBusy || !phone.trim()}
                    className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold"
                    data-testid="chat-send-code-btn"
                  >
                    {gateBusy ? "Sending…" : "Send code"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setWantSupport(false); setGateErr(""); setPhone(""); }}
                    className="w-full text-[11px] text-zinc-500 hover:underline"
                    data-testid="chat-keep-chatting"
                  >
                    ← Keep chatting with {botName}
                  </button>
                </form>
              )}

              {gateStep === "code" && (
                <form onSubmit={submitCode} className="space-y-2">
                  <p className="text-[11px] text-zinc-500">Enter the 6-digit code we texted you.</p>
                  <input
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    placeholder="6-digit code"
                    inputMode="numeric"
                    className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm tracking-widest"
                    data-testid="chat-otp-input"
                    required
                  />
                  {gateErr && <p className="text-[11px] text-red-600" data-testid="chat-gate-error">{gateErr}</p>}
                  <button
                    type="submit"
                    disabled={gateBusy || otp.length < 4}
                    className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold"
                    data-testid="chat-verify-btn"
                  >
                    {gateBusy ? "Verifying…" : "Confirm & sign in"}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setGateStep("phone"); setOtp(""); setGateErr(""); }}
                    className="w-full text-[11px] text-zinc-500 hover:underline"
                    data-testid="chat-change-phone"
                  >
                    Use a different number
                  </button>
                </form>
              )}

              {gateStep === "signup" && (
                <form onSubmit={submitSignup} className="space-y-2">
                  <p className="text-[11px] text-zinc-500">Welcome! Tell us who you are:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      value={signupForm.first_name}
                      onChange={(e) => setSignupForm((f) => ({ ...f, first_name: e.target.value }))}
                      placeholder="First name *"
                      className="h-10 rounded-lg border border-zinc-200 px-3 text-sm"
                      data-testid="chat-first-input"
                      required
                    />
                    <input
                      value={signupForm.last_name}
                      onChange={(e) => setSignupForm((f) => ({ ...f, last_name: e.target.value }))}
                      placeholder="Last name"
                      className="h-10 rounded-lg border border-zinc-200 px-3 text-sm"
                      data-testid="chat-last-input"
                    />
                  </div>
                  <input
                    value={signupForm.email}
                    onChange={(e) => setSignupForm((f) => ({ ...f, email: e.target.value }))}
                    placeholder="Email *"
                    type="email"
                    className="w-full h-10 rounded-lg border border-zinc-200 px-3 text-sm"
                    data-testid="chat-email-input"
                    required
                  />
                  {gateErr && <p className="text-[11px] text-red-600" data-testid="chat-gate-error">{gateErr}</p>}
                  <button
                    type="submit"
                    disabled={gateBusy}
                    className="w-full h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-semibold"
                    data-testid="chat-create-btn"
                  >
                    {gateBusy ? "Creating…" : "Create account & continue"}
                  </button>
                </form>
              )}
            </div>
          ) : (
            <form onSubmit={send} className="border-t border-zinc-200 p-2.5 flex items-center gap-2 bg-white">
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickPhoto}
                data-testid="chat-photo-file"
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="h-10 w-10 shrink-0 rounded-full border border-zinc-200 text-zinc-500 hover:bg-zinc-50 disabled:opacity-50 flex items-center justify-center"
                data-testid="chat-photo-btn"
                aria-label="Attach a photo"
              >
                <Paperclip size={16} />
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask me a question…"
                className="flex-1 h-10 rounded-full border border-zinc-200 px-4 text-sm focus:border-emerald-400 outline-none"
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={sending || !input.trim()}
                className="h-10 w-10 shrink-0 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white flex items-center justify-center"
                data-testid="chat-send-btn"
              >
                <Send size={16} />
              </button>
            </form>
          )}

          {/* Permanent "Talk to support" button */}
          <button
            onClick={() => requestSupport(lastUserText || "Customer support request")}
            className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 py-2 border-t border-zinc-100"
            data-testid="chat-support-btn"
          >
            <LifeBuoy size={13} /> Talk to customer support
          </button>
        </div>
      )}
    </>
  );
}
